/**
 * @see https://umijs.org/docs/max/access#access
 *
 * 以 initialState.authz.permission_keys 为唯一依据（含 system_admin 在 normalizeAuthz 中全量兜底）。
 * 不做 role/access 硬编码回退，全量权限按权限点控制。
 */
export default function access(
  initialState:
    | {
        currentUser?: API.AdminUserResponse;
        authz?: API.AdminUserAuthzResponse;
      }
    | undefined,
) {
  const { authz } = initialState ?? {};
  const permissionKeys = authz?.permission_keys ?? [];
  const permSet = new Set(permissionKeys);

  const can = (permKey: string): boolean => permSet.has(permKey);
  const canAny = (permKeys: string[]): boolean =>
    permKeys.length > 0 && permKeys.some((k) => permSet.has(k));
  const canAll = (permKeys: string[]): boolean =>
    permKeys.length > 0 && permKeys.every((k) => permSet.has(k));
  const canAccessMarketingPush = canAny(['tools.marketing_push.view', 'tools.marketing_push.edit', 'tools.marketing_push.send']);
  const canAccessBotActivationPush = canAny([
    'tools.bot_activation_push.view',
    'tools.bot_activation_push.edit',
  ]);
  const canAccessMobilePushTest = canAny([
    'tools.mobile_push.view',
    'tools.mobile_push.send',
  ]);
  const canAccessTelegramPushTest = canAny([
    'tools.telegram_push.view',
    'tools.telegram_push.send',
  ]);
  const canAccessToolsHomeMarqueeBanners = can(
    'tools.home_marquee_banners.view',
  );
  const canAccessToolsHomeShortcuts = can('tools.home_shortcuts.view');

  return {
    can,
    canAny,
    canAll,
    permissionKeys,
    // 员工管理
    canAccessStaff: can('staff'),
    canAccessStaffList: can('staff.list.view'),
    canAccessStaffDetail: can('staff.detail.view'),
    canAccessStaffRolesPermissions: can('staff.role_permission.view'),
    canAccessStaffOperationLogs: canAny([
      'staff.operation_logs.view',
      'staff.operation_logs.view_all',
    ]),
    // 个人设置
    canAccessAccount: can('account'),
    canAccessAccountSettings: can('account.settings.view'),
    canAccessAccountApiKey: can('account.api_key.view'),
    // 求职用户
    canAccessJobSeekers: can('job_seekers'),
    canAccessJobSeekersUsers: can('job_seekers.users.view'),
    canAccessJobSeekersUserDetail: can('job_seekers.users.detail.view'),
    canAccessJobSeekersUsersCreate: can('job_seekers.users.create'),
    canAccessJobSeekersJobIntentions: can('job_seekers.job_intentions.view'),
    canAccessJobSeekersJobIntentionDetail: can(
      'job_seekers.job_intentions.detail.view',
    ),
    canAccessJobSeekersResumes: can('job_seekers.resumes.view'),
    canAccessJobSeekersResumeDetail: can('job_seekers.resumes.detail.view'),
    canAccessJobSeekersResumeSourceFiles: can(
      'job_seekers.resume_source_files.view',
    ),
    // 企业管理
    canAccessCompanies: can('companies'),
    canAccessCompaniesList: can('companies.list.view'),
    canAccessCompaniesConfig: can('companies.config.view'),
    // 岗位管理
    canAccessJobs: can('jobs'),
    canAccessJobsList: can('jobs.list.view'),
    canJobsReviewAssignmentManage: can('jobs.review_assignment.manage'),
    canAccessJobsReviewRecords: can('jobs.review_records.view'),
    canAccessJobsBlacklist: can('jobs.blacklist.view'),
    canAccessJobsGeneration: can('jobs.generation.view'),
    canAccessJobsGenerationDashboard: can('jobs.generation.dashboard.view'),
    // 配置管理
    canAccessConfig: can('config'),
    canAccessConfigJobPositions: can('config.job_positions.view'),
    canAccessConfigJobGroups: can('config.job_groups.view'),
    canAccessConfigHotJobGroups: can('config.hot_job_groups.view'),
    canAccessConfigHotSearchWords: can('config.hot_search_words.view'),
    canAccessConfigVisitorContactLimit: can(
      'config.visitor_contact_limit.view',
    ),
    canAccessConfigEmployerSalesContacts: can(
      'config.employer_sales_contacts.view',
    ),
    canAccessConfigLocations: can('config.locations.view'),
    canAccessConfigJdComplaintTypes: can('config.jd_complaint_types.view'),
    canAccessConfigJdFeedbackTypes: can('config.jd_feedback_types.view'),
    canAccessConfigBenefitGuaranteeTags: can(
      'config.benefit_guarantee_tags.view',
    ),
    // 反馈与投诉
    canAccessFeedback: can('feedback'),
    canAccessFeedbackList: can('feedback.list.view'),
    canAccessMatchFeedback: can('feedback.match_feedback.view'),
    canAccessIMPresets: can('feedback.im_presets.view'),
    // Existing feedback operators should see the new feedback queue immediately.
    // The dedicated key remains available for future fine-grained role assignment.
    canAccessFeedbackJobTaxonomySearch:
      can('feedback.job_taxonomy_search.view') || can('feedback.list.view'),
    canAccessFeedbackJobComplaints: can('feedback.job_complaints.view'),
    canAccessFeedbackResumeComplaints: can('feedback.resume_complaints.view'),
    canAccessFeedbackAccountAppeals: can('feedback.account_appeals.view'),
    canAccessFeedbackJdFeedbackRecords: can(
      'feedback.jd_feedback_records.view',
    ),
    // 内容管理
    canAccessContent: can('content'),
    canAccessContentMediaPartners: can('content.media_partners.view'),
    canAccessContentMediaPartnerCreate: can('content.media_partners.create'),
    canAccessContentMediaPartnerUpdate: can('content.media_partners.update'),
    canAccessContentMediaPartnerDelete: can('content.media_partners.delete'),
    canAccessContentArticles: can('content.articles.view'),
    canAccessContentArticleCreate: can('content.articles.create'),
    canAccessContentArticleUpdate: can('content.articles.update'),
    canAccessContentArticleDelete: can('content.articles.delete'),
    // Telegram 矩阵
    canAccessTelegram: can('telegram'),
    canAccessTelegramAccounts: can('telegram.accounts.view'),
    canAccessTelegramEntities: can('telegram.entities.view'),
    canAccessTelegramDashboard: can('telegram.dashboard.view'),
    canAccessTelegramRunnerManage: can('telegram.runner.manage'),
    // Facebook 矩阵
    canAccessFacebookMatrix: can('facebook_matrix'),
    canAccessFacebookMatrixEntities: can('facebook_matrix.entities.view'),
    canAccessFacebookMatrixEntityCreate: can('facebook_matrix.entity.create'),
    canAccessFacebookMatrixEntityUpdate: can('facebook_matrix.entity.update'),
    canAccessFacebookMatrixEntityDelete: can('facebook_matrix.entity.delete'),
    // 推广渠道
    canAccessAdChannels: can('ad_channels'),
    // 商业化管理
    canAccessCommercial: can('commercial'),
    canAccessCommercialProducts: can('commercial.products.view'),
    canAccessCommercialBenefits: can('commercial.benefits.view'),
    canAccessCommercialPaywalls: can('commercial.paywalls.view'),
    canAccessCommercialOrders: can('commercial.orders.view'),
    // 运营工具
    canAccessTools:
      can('tools') ||
      canAccessMarketingPush ||
      canAccessBotActivationPush ||
      canAccessMobilePushTest ||
      canAccessTelegramPushTest ||
      canAccessToolsHomeMarqueeBanners ||
      canAccessToolsHomeShortcuts,
    canAccessToolsHomeMarqueeBanners,
    canAccessToolsHomeShortcuts,
    canAccessMobilePushTest,
    canAccessMarketingPush,
    canAccessBotActivationPush,
    canEditBotActivationPush: can('tools.bot_activation_push.edit'),
    canEditMarketingPush: can('tools.marketing_push.edit'),
    canSendMarketingPush: can('tools.marketing_push.send'),
    canSendMobilePushTest: can('tools.mobile_push.send'),
    canAccessTelegramPushTest,
    canSendTelegramPushTest: can('tools.telegram_push.send'),
  };
}
